import { useState } from 'react'

import styled from 'styled-components'

import { ContentContainer, SectionHeader } from '../ViewSelector'

import { IconButtonExpand } from './IconButtonExpand'
import { SelectItem } from './SelectItem'

export interface IViewSelectorOption {
  Id: string
  Content: string | JSX.Element
  Disclaimer?: string
  SubTitle?: string
  OnSelectChild?: (selectedChild: IViewSelectorOption) => void
  OnHoverChild?: (hoverOption: IViewSelectorOption | null) => void
  Options?: IViewSelectorOption[]
}

export const OptionPicker = (props: {
  expanded: boolean
  setExpanded: (newValue: boolean) => void
  viewingItems: IViewSelectorOption
  selectedItem?: IViewSelectorOption
  hoverItem?: IViewSelectorOption | null
  onSelectOption?: (option: IViewSelectorOption) => void
  onHover?: (option: IViewSelectorOption | null) => void
  hideSelectedOptionDisplay?: boolean
  showBottomBorder?: boolean
  collapsedFloors?: Set<string>
  onToggleFloorCollapse?: (floorCode: string) => void
  /** When true, show the left selection bullet on options. Omit for plain lists (e.g. JSX rows like TypeSelector). */
  showMarker?: boolean
  selectedZoneParentFloorCode?: string | null
  emptySelectionLabel?: string
  /**
   * When true, render the option list directly (no selected-value row or inner expand).
   * Visibility is controlled by the parent ViewSelector.
   */
  flat?: boolean
}) => {
  const [childExpanded, setChildExpanded] = useState<boolean>(true)
  const [childSelectedItem, setChildSelectedItem] = useState<
    IViewSelectorOption | undefined
  >()
  const [childHoverOption, setChildHoverOption] =
    useState<IViewSelectorOption | null>(null)

  const hasChildren =
    !!props.selectedItem && !!props.selectedItem.Options?.length

  const onClickExpand = (newValue: boolean) => {
    props.setExpanded(newValue)
    setChildExpanded(false)
  }

  const onSelectItem = (option: IViewSelectorOption) => {
    if (props.onSelectOption) {
      props.onSelectOption(option)
      setChildSelectedItem(undefined)
      setChildHoverOption(null)
    }
  }

  const onSelectChild = (option: IViewSelectorOption) => {
    setChildSelectedItem(option)

    if (props.selectedItem?.OnSelectChild) {
      props.selectedItem.OnSelectChild(option)
    }
  }

  const onHover = (option: IViewSelectorOption | null) => {
    if (props.onHover) {
      props.onHover(option)
    }
  }

  const onHoverChild = (option: IViewSelectorOption | null) => {
    setChildHoverOption(option)

    if (props.selectedItem?.OnHoverChild) {
      props.selectedItem.OnHoverChild(option)
    }
  }

  const sectionHeader = (
    <SectionHeader>
      {props.viewingItems.SubTitle ?? props.viewingItems.Content}
    </SectionHeader>
  )

  const renderOptions = () =>
    props.viewingItems.Options?.map((option, i) => {
      const optionWithMeta = option as IViewSelectorOption & {
        hasZones?: boolean
        isZone?: boolean
        parentFloorCode?: string
      }
      const isZone = optionWithMeta.isZone === true
      const hasZones = optionWithMeta.hasZones === true
      const floorCode = !isZone ? option.Id : undefined
      const isCollapsed =
        floorCode && props.collapsedFloors
          ? props.collapsedFloors.has(floorCode)
          : false
      const onToggleCollapse =
        floorCode && props.onToggleFloorCollapse
          ? props.onToggleFloorCollapse
          : undefined

      const floorContentWithExpand =
        hasZones && floorCode && onToggleCollapse ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>{option.Content}</span>
            <IconButtonExpand
              expanded={!isCollapsed}
              onClick={_expanded => {
                onToggleCollapse(floorCode)
              }}
              size={16}
              style={{
                border: 'none',
                boxShadow: 'none',
                padding: '0',
                margin: '0',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            />
          </div>
        ) : (
          option.Content
        )

      const isSelected = Boolean(
        props.selectedItem?.Id === option.Id ||
          props.hoverItem?.Id === option.Id ||
          (!!props.selectedZoneParentFloorCode &&
            !isZone &&
            option.Id === props.selectedZoneParentFloorCode),
      )

      return (
        <div key={i} style={{ paddingLeft: isZone ? '18px' : '0' }}>
          <SelectItem
            key={i}
            content={floorContentWithExpand}
            disclaimer={option.Disclaimer}
            onClick={
              props.onSelectOption ? () => onSelectItem(option) : undefined
            }
            onMouseEnter={() => onHover(option)}
            onMouseLeave={() => onHover(null)}
            selected={isSelected}
            showMarker={props.showMarker}
            style={{
              border: '1px solid #E8E8E8',
              borderRadius: '4px',
            }}
          />
        </div>
      )
    })

  return (
    <>
      <OptionContainer
        $flat={props.flat}
        showBottomBorder={props.showBottomBorder || hasChildren}
      >
        {props.flat ? (
          <>
            <FlatSelectedHeader>
              {sectionHeader}
              <SelectedOption>
                {props.selectedItem
                  ? props.selectedItem.Content
                  : (props.emptySelectionLabel ?? 'Select')}
              </SelectedOption>
            </FlatSelectedHeader>
            <ContentContainer aria-expanded={props.expanded}>
              {renderOptions()}
            </ContentContainer>
          </>
        ) : (
          <>
            {!props.hideSelectedOptionDisplay && <>{sectionHeader}</>}

            <HeaderContainer>
              {props.hideSelectedOptionDisplay && <>{sectionHeader}</>}
              {!props.hideSelectedOptionDisplay && (
                <SelectedOption>
                  {props.selectedItem
                    ? props.selectedItem.Content
                    : (props.emptySelectionLabel ?? 'Select')}
                </SelectedOption>
              )}

              <IconButtonExpand
                expanded={props.expanded}
                onClick={onClickExpand}
                style={{ display: 'flex' }}
                size={20}
              />
            </HeaderContainer>

            <ContentContainer aria-expanded={props.expanded}>
              {renderOptions()}
            </ContentContainer>
          </>
        )}
      </OptionContainer>

      {!props.flat &&
        props.selectedItem &&
        props.selectedItem.Options &&
        props.selectedItem.Options.length > 0 && (
          <OptionPicker
            expanded={childExpanded}
            setExpanded={setChildExpanded}
            viewingItems={props.selectedItem}
            selectedItem={childSelectedItem}
            hoverItem={childHoverOption}
            onSelectOption={onSelectChild}
            onHover={onHoverChild}
            collapsedFloors={props.collapsedFloors}
            onToggleFloorCollapse={props.onToggleFloorCollapse}
            showMarker={props.showMarker}
            selectedZoneParentFloorCode={props.selectedZoneParentFloorCode}
          />
        )}
    </>
  )
}

const SelectedOption = styled.span`
  font-size: 14px;
  font-weight: 500;
  display: block;
  margin-top: 4px;
  color: #212121;
`

const FlatSelectedHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
  padding-bottom: 8px;
  margin-bottom: 4px;
  border-bottom: 1px solid #e8e8e8;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const OptionContainer = styled.div<{
  showBottomBorder?: boolean
  $flat?: boolean
}>`
  padding: ${props => (props.$flat ? '8px 8px 4px' : '16px 8px 8px 8px')};
  border-radius: 4px;

  ${props =>
    props.showBottomBorder &&
    `
    border-bottom: 1px solid #e8e8e8;
    padding: ${props.$flat ? '8px 8px' : '16px 8px'};`}
`
